import { useCallback, useEffect, useState } from 'react'
import { Button, Icon, Modal, Spinner, useToast } from '@w7/ui'
import {
  changeMatchStatusMaster,
  fetchMemberEvents,
  type MemberEventRow,
} from '@/api/map/memberEvents'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canProductLock } from '@/app/staffAccess'

/**
 * Lab view-member Events column — enable/disable cricket matches for one
 * downline account (hierarchy-service → Market-API member_disabled).
 * Without product-lock, status stays visible but toggles are disabled.
 */
export function MemberEventsModal({
  userId,
  username,
  onClose,
}: {
  userId: string
  username: string
  onClose: () => void
}) {
  const toast = useToast()
  const staffMe = useStaffMe()
  const writeAllowed = staffMe.isSuccess && canProductLock(staffMe.data)
  const [rows, setRows] = useState<MemberEventRow[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await fetchMemberEvents(userId))
    } catch (err) {
      setRows([])
      setError(err instanceof Error ? err.message : 'Failed to load events')
    }
  }, [userId])

  useEffect(() => {
    void load()
  }, [load])

  async function toggle(row: MemberEventRow) {
    if (!writeAllowed || !row.isEdit || busyId) return
    const next = row.status === 'Enable' ? 'Disable' : 'Enable'
    setBusyId(row.id)
    try {
      await changeMatchStatusMaster({ eventId: row.id, userId, status: next })
      toast.success(
        next === 'Enable'
          ? 'The event has been successfully enabled'
          : 'The event has been successfully disabled',
      )
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Events"
      subtitle={username}
      size="lg"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error ? <p className="w7-dimmer">{error}</p> : null}
      {rows == null ? (
        <p className="w7-dimmer">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="w7-dimmer">No Events found</p>
      ) : (
        <div className="w7-events-table-wrap">
          <table className="w7-events-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Open</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.openDate ? new Date(row.openDate).toLocaleString() : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className={`w7-events-lock${
                        row.status === 'Enable' ? ' is-unlocked' : ' is-locked'
                      }${!row.isEdit || !writeAllowed ? ' is-muted' : ''}`}
                      disabled={!writeAllowed || !row.isEdit || busyId === row.id}
                      aria-label={
                        row.status === 'Enable'
                          ? writeAllowed
                            ? 'Unlocked — click to lock'
                            : 'Enabled (view only)'
                          : writeAllowed
                            ? 'Locked — click to unlock'
                            : 'Disabled (view only)'
                      }
                      title={
                        !writeAllowed
                          ? row.status === 'Enable'
                            ? 'Enabled (view only — missing Event Lock/Unlock)'
                            : 'Disabled (view only — missing Event Lock/Unlock)'
                          : row.isEdit
                            ? row.status === 'Enable'
                              ? 'Disable for this user'
                              : 'Enable for this user'
                            : 'Locked by upline'
                      }
                      onClick={() => void toggle(row)}
                    >
                      {busyId === row.id ? (
                        <Spinner size={15} />
                      ) : (
                        <Icon
                          name={row.status === 'Enable' ? 'unlock' : 'lock'}
                          size={18}
                        />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}
